import React, { useEffect, useState, useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { useEmbeddedEffect } from '@machinemetrics/mm-react-embeddable';
import i18n from 'i18next';
import { useTranslation } from 'react-i18next';
import { Box } from '@chakra-ui/react';
import { palette, Icon } from '@m12s/component-library';
import { falTimes } from 'lib/icons';
import { IconWrapper } from 'components/SwipeUpModal';

import {
  actionCloseLaborTicketModal,
  actionSetEmbeddedLaborTicket,
} from 'lib/actions';
import { getCurrentLaborTicket } from 'lib/selectors/getLaborTickets';
import { getLaborTicketAction } from 'lib/selectors/getLaborTicketAction';
import { getLaborTicketModalOpen } from 'lib/selectors/getLaborTicketModalOpen';
import { getIsAuthorizationModalOpen } from 'lib/selectors/getIsAuthorizationModalOpen';
import { LABOR_TICKET_HOST } from 'lib/appConfig';
import { getEmbeddedLaborTicket } from 'lib/selectors/getEmbeddedLaborTicket';
import { useRequestRecentActivitySets } from 'lib/hooks';
import { getMachine } from 'lib/selectors/getMachine';
import { getEnableOperatorVerification } from 'lib/selectors/getEnableOperatorVerification';
import { getLaborScopeTotalParts } from 'lib/selectors/getScopeTotalParts';
import { getLaborTicketPartsLoaded } from 'lib/selectors/getLaborTicketPartsLoaded';
import { getIsApmERPJobFlow } from 'lib/selectors/getIsApmERPJobFlow';
import { getApmCardPartsContext } from 'lib/selectors/getApmCardPartsContext';
import { getIsSignOut } from 'lib/selectors/getIsSignOut';
import { getFAICurrentInspection } from 'lib/selectors/getFAICurrentInspection';
import { getFAIEnabled } from 'lib/selectors/getFAIEnabled';
import { getTimeOptionsContext } from 'lib/selectors/getTimeOptionsContext';
import { FAI_STATUS } from 'lib/constants';
import { isIndirectTicket } from 'lib/constants/laborTicketTypes';
import useHandleLaborTicketSubmit from '../LaborTicket/hooks/useHandleLaborTicketSubmit';
import MMEmbeddableZoneWithLoader from '../MMEmbeddableZoneWithLoader';
import MMDrawer from '../MMDrawer';

const EmbeddedLaborTicket = ({
  id,
  path,
  personId,
  splitTime,
  newWorkOrderOperationRef,
}) => {
  const dispatch = useDispatch();
  const { refetch: refetchRecentActivitySets } = useRequestRecentActivitySets();
  const laborTicketAction = useSelector(getLaborTicketAction);
  const machine = useSelector(getMachine);
  const enableOperatorVerification = useSelector(getEnableOperatorVerification);
  const laborScopeTotalParts = useSelector(getLaborScopeTotalParts);
  const laborTicketPartsLoaded = useSelector(getLaborTicketPartsLoaded);
  const laborTicketModalOpen = useSelector(getLaborTicketModalOpen);
  const machineRef = machine?.machineRef;

  // The labor ticket form is a confirmation snapshot: the count it shows must reflect
  // parts made as of when the form opened and must not drift as new parts arrive while
  // it's open (otherwise the embedded form resets under the operator). The on-screen
  // "unsubmitted" tile stays live via getLaborScopeTotalParts; only the value handed to
  // the embed is frozen. Pin the first authoritative (loaded) count once the form opens,
  // and release it on close. Mirrors the pinnedLaborTicket pattern below.
  const [pinnedLaborScopeTotalParts, setPinnedLaborScopeTotalParts] = useState(
    null
  );
  useEffect(() => {
    if (!laborTicketModalOpen) {
      setPinnedLaborScopeTotalParts(null);
    } else if (
      laborTicketPartsLoaded &&
      machineRef &&
      pinnedLaborScopeTotalParts === null
    ) {
      setPinnedLaborScopeTotalParts(laborScopeTotalParts);
    }
  }, [
    laborTicketModalOpen,
    laborTicketPartsLoaded,
    machineRef,
    laborScopeTotalParts,
    pinnedLaborScopeTotalParts,
  ]);

  const laborScopeTotalPartsByMachine = useMemo(() => {
    // Until the subscription has delivered a count for this clock-in we have no
    // authoritative value, so omit the machine key and let the embedded form fall back
    // to its own part-count query instead of a stale 0. Once pinned, the value is frozen
    // at form-open so the form doesn't reset as new parts come in.
    if (!machineRef || pinnedLaborScopeTotalParts === null) {
      return {};
    }

    return { [machineRef]: pinnedLaborScopeTotalParts };
  }, [machineRef, pinnedLaborScopeTotalParts]);
  const isAPMControlled = useSelector(getIsApmERPJobFlow);
  const isSignOut = useSelector(getIsSignOut);
  const timeOptionsContext = useSelector(getTimeOptionsContext);

  const faiEnabled = useSelector(getFAIEnabled);
  const currentInspection = useSelector(getFAICurrentInspection);
  const apmParts = useSelector(getApmCardPartsContext);

  // false if current inspection is pending or in progress
  const blockCompleteSetup = useMemo(() => {
    return (
      faiEnabled &&
      (currentInspection?.status === FAI_STATUS.PENDING ||
        currentInspection?.status === FAI_STATUS.IN_PROGRESS)
    );
  }, [currentInspection, faiEnabled]);

  // Create close modal callback for the submit handler
  const closeModal = useCallback(() => {
    dispatch(actionCloseLaborTicketModal());
    dispatch(actionSetEmbeddedLaborTicket(null));
  }, [dispatch]);

  const handleSubmit = useHandleLaborTicketSubmit(closeModal);

  useEmbeddedEffect(id, 'labor-ticket-form:close', () => {
    dispatch(actionCloseLaborTicketModal());
    // Refetch activity sets to ensure state is in sync after embedded form closes
    refetchRecentActivitySets();
  });

  // No longer refresh on not-found; the embedded app will close itself

  useEmbeddedEffect(id, 'labor-ticket-form:submit', handleSubmit);

  // The indirect flow emits its own events, so the handlers above never fire
  // for it. Closing a ticket there needs no part reconciliation.
  useEmbeddedEffect(id, 'indirect-labor:close', () => {
    closeModal();
    refetchRecentActivitySets();
  });

  useEmbeddedEffect(id, 'indirect-labor:submit', () => {
    closeModal();
    refetchRecentActivitySets();
  });

  return (
    <MMEmbeddableZoneWithLoader
      id={id}
      src={`${LABOR_TICKET_HOST}`}
      path={path}
      context={{
        personId,
        action: laborTicketAction,
        enableOperatorVerification,
        opened: laborTicketModalOpen,
        openedAt: splitTime || undefined,
        // Deploy labor-ticket-form (mm-integrations #418) before or with this app so
        // the embed reads unsubmittedLaborPartsByMachine. laborScopeTotalPartsByMachine
        // is kept during rollout for older form builds.
        unsubmittedLaborPartsByMachine: laborScopeTotalPartsByMachine,
        laborScopeTotalPartsByMachine,
        isAPMControlled,
        isSignOut,
        newWorkOrderOperationRef: newWorkOrderOperationRef || undefined,
        allowCompleteSetup: !blockCompleteSetup,
        timeOptionsContext,
        // APM submit defaults are owned by Dashboard/program-event quantities.
        partsContext: apmParts ? { apmParts } : undefined,
      }}
      params={{
        language: i18n.language,
      }}
    />
  );
};

EmbeddedLaborTicket.propTypes = {
  id: PropTypes.string.isRequired,
  path: PropTypes.string,
  personId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  splitTime: PropTypes.string,
  newWorkOrderOperationRef: PropTypes.number,
};

const LaborTicketWrapper = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const id = 'labor-ticket-form';
  const embeddedLaborTicket = useSelector(getEmbeddedLaborTicket);
  const currentLaborTicket = useSelector(getCurrentLaborTicket);
  const laborTicketFormOpen = useSelector(getLaborTicketModalOpen);
  const authorizationModalOpen = useSelector(getIsAuthorizationModalOpen);

  const liveLaborTicket = embeddedLaborTicket || currentLaborTicket;

  // Shared by the drawer's own dismiss and the indirect close button, so a
  // manual dismiss cannot drift from an overlay tap.
  const dismissDrawer = useCallback(() => {
    dispatch(actionCloseLaborTicketModal());
  }, [dispatch]);

  // Pin the labor ticket when modal opens to prevent race condition
  // where subscription clears the ticket before the embedded form response arrives
  const [pinnedLaborTicket, setPinnedLaborTicket] = useState(null);

  useEffect(() => {
    if (laborTicketFormOpen && liveLaborTicket && !pinnedLaborTicket) {
      // Pin the ticket when modal opens
      setPinnedLaborTicket(liveLaborTicket);
    } else if (!laborTicketFormOpen && pinnedLaborTicket) {
      // Unpin when modal closes
      setPinnedLaborTicket(null);
    }
  }, [laborTicketFormOpen, liveLaborTicket, pinnedLaborTicket]);

  // Use pinned ticket while modal is open, fall back to live ticket
  const laborTicket = pinnedLaborTicket || liveLaborTicket;
  const laborTicketRef = laborTicket?.laborTicketRef;
  const splitQs = laborTicket?.splitTime
    ? `?splitTime=${encodeURIComponent(laborTicket.splitTime)}`
    : '';
  // An indirect ticket has no parts, job status or intervals to confirm, so it
  // gets /indirect-labor -- which resolves the operator's open one and shows
  // its own confirm panel -- instead of the production form.
  const isIndirect = isIndirectTicket(laborTicket);
  const path = isIndirect
    ? '/indirect-labor'
    : laborTicketRef
    ? `/labor-ticket/${laborTicketRef}${splitQs}`
    : null;

  useEffect(() => {
    if (authorizationModalOpen) {
      dispatch(actionCloseLaborTicketModal());
    }
  }, [authorizationModalOpen, dispatch]);

  if (!laborTicket || !path) {
    return null;
  }

  return (
    <MMDrawer
      isOpen={laborTicketFormOpen}
      onClose={dismissDrawer}
      // The indirect embed renders its own "Confirm indirect labor" header;
      // keeping ours would stack two headers and two close buttons.
      hideHeader={isIndirect}
      ariaLabel={isIndirect ? t('Confirm indirect labor') : undefined}
      title={t('Confirm work produced')}
    >
      {/* hideHeader drops the drawer's only close button, and the embed renders
          one solely on its confirm step -- the pickers and error panels would
          otherwise be dismissable only by an overlay tap. Floated over the
          embed's own header row, as the kiosk flyup does. */}
      {isIndirect && (
        <Box position="absolute" top="0.75rem" right="1rem" zIndex={1}>
          <IconWrapper
            type="button"
            aria-label={t('Close')}
            onClick={dismissDrawer}
          >
            <Icon
              size="2rem"
              color={palette.Grey700}
              icon={falTimes}
              cursor="pointer"
            />
          </IconWrapper>
        </Box>
      )}
      <EmbeddedLaborTicket
        id={id}
        path={path}
        personId={laborTicket.personId}
        splitTime={laborTicket.splitTime || undefined}
        newWorkOrderOperationRef={
          laborTicket.newWorkOrderOperationRef || undefined
        }
      />
    </MMDrawer>
  );
};

export default LaborTicketWrapper;
